/**
 * Kind resolution. This is the whole reason ~6 components cover ~40 formats.
 *
 * A renderer is chosen by the format's KIND. Never by instrument, never by
 * screen. Filling in AUDIT's, ISI's or EPDS's option sets later must add zero
 * frontend code, and it does: those formats already say kind "ordinal", they
 * just have an empty option list today.
 */

export const KIND = {
  ORDINAL: 'ordinal',
  BINARY: 'binary',
  NUMERIC: 'numeric',
  TIME: 'time',
  CATEGORICAL: 'categorical',
  BINARY_PLUS_TEXT: 'binary_plus_text',
}

/** Kinds whose control is built out of a config-supplied option list. */
const OPTION_KINDS = new Set([KIND.ORDINAL, KIND.BINARY, KIND.CATEGORICAL, KIND.BINARY_PLUS_TEXT])

/**
 * `binary_plus_text` is its own kind, and that is what config ships today
 * (config CONTRACT.md v1 fixed it; it briefly shipped as `kind: "binary"` +
 * `free_text: true`). The old shape still resolves here on purpose: it is one
 * line, and it means a rollback of that file cannot break the renderer.
 */
export function resolveKind(format) {
  if (!format || typeof format !== 'object') return null
  if (format.kind === KIND.BINARY_PLUS_TEXT) return KIND.BINARY_PLUS_TEXT
  if (format.kind === KIND.BINARY && format.free_text === true) return KIND.BINARY_PLUS_TEXT
  return format.kind || null
}

export function optionsOf(format) {
  return Array.isArray(format?.options) ? format.options : []
}

export function kindUsesOptions(kind) {
  return OPTION_KINDS.has(kind)
}

/**
 * False when config cannot yet answer "what does this item show" — an unknown
 * kind, or an option kind with no options (the 18 AUDIT/ISI/EPDS stubs).
 * The screen must not render a half-control; it shows a neutral placeholder and
 * refuses to treat the item as answerable.
 */
export function isRenderable(format) {
  const kind = resolveKind(format)
  if (!kind || !Object.values(KIND).includes(kind)) return false
  if (kindUsesOptions(kind) && optionsOf(format).length === 0) return false
  return true
}

/**
 * What an option shows.
 *
 * Some scales are endpoint-anchored: `edeq_influence_0_6` labels 0 and 6 and
 * leaves 1-5 as empty strings, because the source gives no words for them.
 * Rendering those as five identical unnamed radios would leave the participant
 * nothing to tell them apart and would give a screen reader nothing to say, so
 * an unlabelled point falls back to its own value as the marker.
 *
 * This is the ONLY place a coded value is ever shown, and only when config
 * supplies no words at all. A labelled option never shows its number.
 */
export function optionDisplay(option) {
  const label = typeof option?.label === 'string' ? option.label.trim() : ''
  if (label) return { text: label, isFallback: false }
  return { text: String(option?.value ?? ''), isFallback: true }
}

/** Option values arrive as numbers from config and as text from the server. */
export function sameValue(a, b) {
  if (a === null || a === undefined || b === null || b === undefined) return false
  return String(a) === String(b)
}

/**
 * THE BEEP RULE. Do not "fix" this to match day 0 — they differ on purpose.
 *
 * Inside a beep, SF01-SF06 are the only items that cannot be left unanswered.
 * Everything else may be skipped and a partial beep is a valid submission:
 * eight to twelve items, six times a day, five days, on a phone, often while
 * unwell. Someone answering two items at a bus stop must be able to submit two
 * items. Build spec s6 — "Six items, SF01 to SF06. Not skippable" — is the one
 * explicit skippability rule in any source, and it is about these six.
 *
 * DAY 0 DOES NOT USE THIS. There, required-ness comes from the server
 * (required_outstanding per section, optional_item_ids) and nothing carries
 * is_safety. ItemStack and QuestionnaireBody fall back to this function only
 * when requiredIds is absent, which is exactly the beep path.
 *
 * Architect ruling 2026-08-08, after critic's finding 6 observed the two paths
 * implementing the rule oppositely and warned that whoever tried to make them
 * consistent would change one and be wrong. See architecture.md "SCOPE" and
 * frontend/CONTRACT.md.
 */
export function isRequired(item) {
  return item?.is_safety === true
}

/** An answer is {value, text}. Empty means the participant left it blank. */
export function isAnswered(answer) {
  return answer !== null && answer !== undefined && answer.value !== null && answer.value !== undefined && answer.value !== ''
}
